import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, UserPlus, KeyRound, Copy, RefreshCw, Loader2, Eye, BadgeCheck, Award, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

const PERMISSION_OPTIONS = [
  { key: "internships", label: "Internships (manage applications, certificates)" },
  { key: "tests",       label: "Tests (test series, exam attempts, results)" },
  { key: "payments",    label: "Payments (donations, transactions)" },
  { key: "gallery",     label: "Gallery (upload, manage photos)" },
];

function randomPassword(len = 10) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function AdminUsers({ role, title }) {
  const { formatApiError } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [memberUser, setMemberUser] = useState(null);
  const [memberForm, setMemberForm] = useState({
    member_id: "", member_role_label: "", permissions: [], notify_email: true,
  });
  const [form, setForm] = useState({
    name: "", email: "", password: randomPassword(), phone: "",
    college: "", skills: "", interest: "both", send_email: true,
  });
  const [resetForm, setResetForm] = useState({ password: "", send_email: true });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const r = await api.get(`/admin/users${role ? `?role=${role}` : ""}`);
    setItems(r.data || []);
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const openCreate = () => {
    setForm({
      name: "", email: "", password: randomPassword(), phone: "",
      college: "", skills: "", interest: "both", send_email: true,
    });
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/users", { ...form, role: role || "student" });
      toast.success(form.send_email ? `${(role || "Student").replace(/^./, c => c.toUpperCase())} created — credentials emailed` : "User created");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const openReset = (u) => {
    setResetUser(u);
    setResetForm({ password: randomPassword(), send_email: true });
  };

  const submitReset = async () => {
    if (!resetForm.password || resetForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/admin/users/${resetUser.id}/reset-password?new_password=${encodeURIComponent(resetForm.password)}&send_email=${resetForm.send_email}`);
      toast.success(resetForm.send_email ? "Password reset — emailed to user" : "Password reset");
      setResetUser(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const openMember = (u) => {
    setMemberUser(u);
    setMemberForm({
      member_id: u.member_id || "",
      member_role_label: u.member_role_label || "",
      permissions: u.permissions || [],
      notify_email: !u.member_id, // notify only on first issue by default
    });
  };

  const togglePerm = (key) => {
    setMemberForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const submitMember = async () => {
    const id = (memberForm.member_id || "").trim();
    if (id.length < 2) { toast.error("Member ID is required"); return; }
    setSubmitting(true);
    try {
      await api.put(`/admin/users/${memberUser.id}/member-id`, {
        member_id: id,
        member_role_label: memberForm.member_role_label,
        permissions: memberForm.permissions,
        notify_email: memberForm.notify_email,
      });
      toast.success(memberUser.member_id ? "Member ID updated" : "Member ID assigned");
      setMemberUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  const revokeMember = async (u) => {
    if (!window.confirm(`Revoke Member ID ${u.member_id} from ${u.name}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}/member-id`);
      toast.success("Member ID revoked");
      load();
    } catch { toast.error("Failed to revoke"); }
  };

  const filtered = items.filter((u) => !q || u.name?.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase()) || (u.member_id || "").toLowerCase().includes(q.toLowerCase()));
  const canCreate = role === "student" || role === "volunteer";
  const roleLabel = (role || "user").replace(/^./, (c) => c.toUpperCase());

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="heading text-2xl font-bold">{title}</h1>
        <div className="flex items-center gap-2">
          <input
            placeholder="Search..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm w-56"
            data-testid="users-search-input"
          />
          {canCreate && (
            <button
              onClick={() => navigate(`/register?admin_create=1${role ? `&role=${role}` : ""}`)}
              className="bg-brand hover:bg-[#172b66] text-white font-bold rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1.5"
              data-testid="create-user-button"
            >
              <UserPlus size={15} /> Create {roleLabel}
            </button>
          )}
        </div>
      </div>
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Name</th>
              <th className="p-3 font-semibold">Email</th>
              <th className="p-3 font-semibold">Phone</th>
              <th className="p-3 font-semibold">College</th>
              <th className="p-3 font-semibold">Member ID</th>
              <th className="p-3 font-semibold">Role</th>
              <th className="p-3 font-semibold">Joined</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 transition" data-testid={`user-row-${u.id}`}>
                <td className="p-3 font-semibold">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                      {u.photo_url ? (
                        <img src={ABS(u.photo_url)} alt={u.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 font-black text-sm">
                          {(u.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      {role === "student" ? (
                        <Link to={`/admin/students/${u.id}`} className="text-brand hover:underline" data-testid={`view-student-${u.id}`}>
                          {u.name}
                        </Link>
                      ) : (
                        <span>{u.name}</span>
                      )}
                      <div className="flex items-center gap-1 mt-0.5">
                        {u.created_by_admin && <span className="text-[9px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Admin-created</span>}
                        {u.member_id && (
                          <span className="text-[9px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5" data-testid={`pmr-member-badge-${u.id}`}>
                            <BadgeCheck size={9} /> PMR Member
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-slate-600">{u.email}</td>
                <td className="p-3 text-slate-600">{u.phone || "—"}</td>
                <td className="p-3 text-slate-600">{u.college || "—"}</td>
                <td className="p-3" data-testid={`member-id-cell-${u.id}`}>
                  {u.member_id ? (
                    <div>
                      <div className="font-mono font-bold text-emerald-700 text-xs">{u.member_id}</div>
                      {u.member_role_label && <div className="text-[10px] text-slate-500 mt-0.5">{u.member_role_label}</div>}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3"><span className="text-[10px] px-2 py-1 rounded bg-blue-50 text-brand font-bold uppercase">{u.role}</span></td>
                <td className="p-3 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {role === "student" && (
                    <Link to={`/admin/students/${u.id}`} className="text-slate-600 hover:bg-slate-100 p-2 rounded mr-1 inline-block" title="View profile" data-testid={`view-user-${u.id}`}>
                      <Eye size={14} />
                    </Link>
                  )}
                  <button onClick={() => openMember(u)} className="text-emerald-700 hover:bg-emerald-50 p-2 rounded mr-1" title={u.member_id ? "Edit Member ID" : "Assign Member ID"} data-testid={`assign-member-${u.id}`}>
                    <Award size={14} />
                  </button>
                  {u.member_id && (
                    <button onClick={() => revokeMember(u)} className="text-amber-600 hover:bg-amber-50 p-2 rounded mr-1" title="Revoke Member ID" data-testid={`revoke-member-${u.id}`}>
                      <X size={14} />
                    </button>
                  )}
                  <button onClick={() => openReset(u)} className="text-brand hover:bg-blue-50 p-2 rounded mr-1" title="Reset password" data-testid={`reset-pass-${u.id}`}>
                    <KeyRound size={14} />
                  </button>
                  <button onClick={() => del(u.id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-user-${u.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-slate-500">No users found.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create user modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-user-modal">
          <DialogHeader>
            <DialogTitle>Create {roleLabel} Account</DialogTitle>
            <DialogDescription>
              Admin-created {roleLabel.toLowerCase()} accounts are auto-approved. Credentials can be emailed automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Full name *" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="create-user-name" />
            <Field label="Email *" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="create-user-email" />
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Password *</label>
              <div className="mt-1 flex gap-2">
                <input
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 focus:border-brand outline-none font-mono text-sm"
                  data-testid="create-user-password"
                />
                <button type="button" onClick={() => setForm({ ...form, password: randomPassword() })} className="px-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-600" title="Generate new">
                  <RefreshCw size={14} />
                </button>
                <button type="button" onClick={() => copy(form.password)} className="px-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-600" title="Copy">
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} testid="create-user-phone" />
              <Field label="College" v={form.college} on={(v) => setForm({ ...form, college: v })} testid="create-user-college" />
            </div>
            {role === "student" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Interest</label>
                <select
                  value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="create-user-interest"
                >
                  <option value="both">Both Internship &amp; Test Series</option>
                  <option value="internship">Internship only</option>
                  <option value="test_series">Test Series only</option>
                </select>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_email}
                onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                data-testid="create-user-send-email"
              />
              Email these login credentials to the {roleLabel.toLowerCase()}
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm font-semibold" data-testid="create-user-cancel">Cancel</button>
            <button
              onClick={submitCreate}
              disabled={submitting}
              className="bg-brand hover:bg-[#172b66] text-white font-bold rounded-lg px-5 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="create-user-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />} Create {roleLabel}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member ID modal */}
      <Dialog open={!!memberUser} onOpenChange={(o) => !o && setMemberUser(null)}>
        <DialogContent className="max-w-lg" data-testid="member-id-modal">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <BadgeCheck size={18} className="text-emerald-600" />
              {memberUser?.member_id ? "Edit Member" : "Assign PMR Member ID"} — {memberUser?.name}
            </DialogTitle>
            <DialogDescription>
              Member IDs are reserved for PMR Welfare Foundation organisation members. Admin decides what permissions the member gets.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Member ID *</label>
              <input
                value={memberForm.member_id}
                onChange={(e) => setMemberForm({ ...memberForm, member_id: e.target.value.toUpperCase() })}
                placeholder="PMR-MEM-001"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-emerald-600 outline-none text-sm font-mono"
                data-testid="member-id-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Unique across the platform. Letters, numbers and dashes recommended.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Member Role / Title</label>
              <input
                value={memberForm.member_role_label}
                onChange={(e) => setMemberForm({ ...memberForm, member_role_label: e.target.value })}
                placeholder="Field Coordinator, Volunteer Lead, etc."
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm"
                data-testid="member-role-label-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Module Access (Permissions)</label>
              <div className="mt-2 space-y-2">
                {PERMISSION_OPTIONS.map((p) => (
                  <label key={p.key} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={memberForm.permissions.includes(p.key)}
                      onChange={() => togglePerm(p.key)}
                      className="mt-1"
                      data-testid={`member-perm-${p.key}`}
                    />
                    <div>
                      <div className="font-semibold text-slate-800 text-xs uppercase tracking-wider">{p.key}</div>
                      <div className="text-xs text-slate-500">{p.label}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">No checkbox selected = view-only member (login allowed, no module access).</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={memberForm.notify_email}
                onChange={(e) => setMemberForm({ ...memberForm, notify_email: e.target.checked })}
                data-testid="member-notify-email"
              />
              Email the user about their Member ID
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setMemberUser(null)} className="px-4 py-2 text-sm font-semibold" data-testid="member-id-cancel">Cancel</button>
            <button
              onClick={submitMember}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg px-5 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="member-id-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {memberUser?.member_id ? "Update Member" : "Assign Member ID"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password modal */}
      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent className="max-w-md" data-testid="reset-pass-modal">
          <DialogHeader>
            <DialogTitle>Reset Password — {resetUser?.name}</DialogTitle>
            <DialogDescription>Set a new password for {resetUser?.email}.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">New password</label>
              <div className="mt-1 flex gap-2">
                <input
                  value={resetForm.password}
                  onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 font-mono text-sm"
                  data-testid="reset-pass-input"
                />
                <button onClick={() => setResetForm({ ...resetForm, password: randomPassword() })} className="px-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-600">
                  <RefreshCw size={14} />
                </button>
                <button onClick={() => copy(resetForm.password)} className="px-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-600">
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={resetForm.send_email}
                onChange={(e) => setResetForm({ ...resetForm, send_email: e.target.checked })}
                data-testid="reset-send-email"
              />
              Email the new password to the user
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setResetUser(null)} className="px-4 py-2 text-sm font-semibold">Cancel</button>
            <button
              onClick={submitReset}
              disabled={submitting}
              className="bg-brand hover:bg-[#172b66] text-white font-bold rounded-lg px-5 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="reset-pass-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />} Reset Password
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Field({ label, v, on, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand outline-none text-sm"
        data-testid={testid}
      />
    </div>
  );
}
